import { Badge, Box, Button, Card, Container, Group, Stack, Text, Title } from "@mantine/core";
import { Link } from "react-router-dom";
import { FiArrowRight, FiLock } from "react-icons/fi";
import { useAppSelector } from "../app/hooks";
import {
  selectAvailableModulesForCurrentUserInCurrentProject,
  selectHttpProject,
  selectIsCurrentUserAccessReady,
  selectVisibleModulesForCurrentProject,
} from "../projectSelectors";
import { useInternalMsalAccount } from "../hooks/useInternalMsalAccount";
import { MODULE_ICON_BY_KEY } from "../DesignTab/designModuleIcons";
import { DisciplineBadge } from "../components/DisciplinBadge";
import { modulePageUrl } from "./moduleNavigation";
import type { IMultiplacerModuleKey } from "@multiconsult-group/multiplacer-core";
import classes from "./ModuleSelectionPage.module.css";

const MODULE_SUMMARIES: Record<IMultiplacerModuleKey, string> = {
  CEILING:
    "Plasser ventiler, armaturer og tekniske enheter automatisk med AI-støttet himlingsprosjektering.",
  ROOM_VALIDATION: "Sammenlign rommets artikkelgrunnlag med forekomster i dRofus, og finn mangler og avvik.",
  EXPLORE: "Naviger i modellen, søk etter rom og komponenter, og utforsk prosjektets data.",
  ROOM_NUMBERING: "Beregn og kvalitetssikre romnummer med felles regler for hele prosjektet.",
  DROFUS_CHANGE_MANAGEMENT: "Følg opp nye, fjernede og endrede rom og artikler i dRofus.",
};

export function ModuleSelectionPage() {
  const project = useAppSelector(selectHttpProject);
  const modules = useAppSelector(selectVisibleModulesForCurrentProject);
  const available = useAppSelector(selectAvailableModulesForCurrentUserInCurrentProject);
  const ready = useAppSelector(selectIsCurrentUserAccessReady);
  const internal = useInternalMsalAccount();
  return (
    <Box className={classes.page}>
      <Container size={1240} className={classes.container}>
        <Stack gap={28}>
          <Stack gap={10} className={classes.heading}>
            <Text size="sm" c="dimmed" fw={500}>
              {project?.title} · {project?.projectNo}
            </Text>
            <Title
              order={1}
              size={32}
              fw={650}
              style={{ letterSpacing: "-0.8px", lineHeight: 1.2 }}
            >
              Velg modul
            </Title>
            <Text size="sm" c="dimmed">
              Velg verktøyet du trenger. Du kan bytte modul når som helst.
            </Text>
          </Stack>
          {modules.length === 0 && <Text>Ingen moduler er tilgjengelige i dette prosjektet.</Text>}
          <div className={classes.grid}>
            {modules.map((module) => {
              const Icon = MODULE_ICON_BY_KEY[module.key];
              const canUse = (ready || internal) && (internal || available.includes(module.key));
              return (
                <Card
                  key={module.key}
                  withBorder
                  radius="md"
                  p={24}
                  className={classes.card}
                  data-available={canUse || undefined}
                >
                  <Stack gap={16} h="100%">
                    <Group justify="space-between">
                      <Box className={classes.icon}>
                        <Icon size={23} aria-hidden="true" />
                      </Box>
                      {!canUse && (
                        <Badge color="gray" variant="light" leftSection={<FiLock />}>
                          {ready || internal ? "Ingen tilgang" : "Laster tilgang"}
                        </Badge>
                      )}
                    </Group>
                    <Title order={2} size={21} fw={600} className={classes.cardTitle}>
                      {module.title}
                    </Title>
                    <Text size="sm" c="dimmed" className={classes.description}>
                      {MODULE_SUMMARIES[module.key]}
                    </Text>
                    <Group gap={6} mt="auto">
                      {module.disciplines.map((discipline) => (
                        <DisciplineBadge key={discipline} discipline={discipline} />
                      ))}
                    </Group>
                    {canUse && project ? (
                      <Button
                        component={Link}
                        to={modulePageUrl(project.projectNo, module.key)}
                        aria-label={`Åpne ${module.title}`}
                        variant="light"
                        className={classes.openButton}
                        rightSection={<FiArrowRight aria-hidden="true" />}
                        fullWidth
                      >
                        Åpne modul
                      </Button>
                    ) : (
                      <Button
                        disabled
                        fullWidth
                        variant="default"
                        color="gray"
                        className={`${classes.openButton} ${classes.unavailableButton}`}
                        leftSection={ready || internal ? <FiLock aria-hidden="true" /> : undefined}
                      >
                        {ready || internal ? "Ingen tilgang" : "Laster tilgang…"}
                      </Button>
                    )}
                  </Stack>
                </Card>
              );
            })}
          </div>
        </Stack>
      </Container>
    </Box>
  );
}
